/**
 * /portal/login — pure sign-in (email + password + Forgot password).
 * No Google OAuth. No register toggle. Directs new orgs to /portal/register.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogIn, Lock, Mail, ArrowRight } from 'lucide-react';
import { usePortalStore, toast } from '../../stores';
import { portalAuthApi } from '../../services/api';
import { NexusButton } from '../../components/nexus-ui/NexusButton';
import { NexusInput } from '../../components/nexus-ui/NexusInput';
import { NexusCard } from '../../components/nexus-ui/NexusCard';
import { CognaraLogo } from '../../components/instrument/CognaraLogo';
import DnaHelix from '../../components/instrument/DnaHelix';

const PortalLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setAuth, isAuthenticated } = usePortalStore();

  useEffect(() => { if (isAuthenticated) navigate('/portal/dashboard', { replace: true }); }, [isAuthenticated, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const r = await portalAuthApi.login(email, password);
      const d = r.data;
      setAuth({ token: d.access_token, user: d.user, tenant: d.tenant, authType: 'jwt' });
      toast.success(`Welcome back, ${d.user?.name || d.user?.email}`);
      navigate('/portal/dashboard', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Invalid email or password');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" style={{ background: 'var(--nexus-bg-primary)' }}>
      {/* Procedural double-helix backdrop — glowing steel-blue strands +
          ember rungs, slowly rotating. Pure Three.js (no model files).
          z-0 places it behind the card; pointer-events-none so it never
          intercepts form clicks. */}
      <div className="pointer-events-none absolute inset-0 z-0 opacity-60">
        <DnaHelix />
      </div>
      {/* Radial vignette to keep focus on the form even on bright screens */}
      <div className="pointer-events-none absolute inset-0 z-0"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 35%, rgba(8, 12, 24, 0.72) 100%)',
        }}
      />
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }} className="w-full max-w-md z-10">
        <NexusCard variant="glass" hover={false} className="p-8">
          <div className="text-center mb-7">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-3"
              style={{ background: 'var(--layer-2)', border: '1px solid var(--hairline)', color: 'var(--nexus-text-primary)' }}>
              <CognaraLogo variant="icon" size={28} />
            </div>
            <h1 className="font-heading text-2xl font-semibold text-nexus-text-primary">Welcome back</h1>
            <p className="text-[13px] text-nexus-text-secondary mt-1.5">Sign in to your Cognara workspace</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <NexusInput label="Work email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com" icon={<Mail size={14} />} data-testid="portal-email" autoFocus required />
            <NexusInput label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password" icon={<Lock size={14} />} error={error} data-testid="portal-password" required />
            <div className="flex items-center justify-end text-[12px] -mt-1">
              <Link to="/portal/forgot-password" className="text-nexus-accent-blue hover:opacity-80" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
            <NexusButton type="submit" className="w-full" isLoading={loading} data-testid="portal-submit">
              <LogIn size={14} /> Sign in
            </NexusButton>
          </form>

          <div className="mt-6 pt-6 border-t text-center text-[12px] text-nexus-text-secondary" style={{ borderColor: 'var(--hairline)' }}>
            New to Cognara?{' '}
            <Link to="/portal/register" className="font-semibold text-nexus-accent-orange inline-flex items-center gap-1" data-testid="go-register">
              Create an organization <ArrowRight size={12} />
            </Link>
          </div>
          <p className="text-center text-[10px] text-nexus-text-tertiary mt-4">
            Have an invite? Open the link from your email or paste the activation URL.
          </p>
        </NexusCard>
      </motion.div>
    </div>
  );
};

export default PortalLogin;
